/**
 * Biometrie Koken — Face ID / Touch ID pour la restauration de session.
 *
 * (23/08) EMANCIPATION DE MEDIAN, option A choisie par le fondateur : on garde
 * le gain d'un tap (Face ID deverrouille le jeton de session), mais le natif
 * est desormais NOTRE plugin (KokenBiometric.swift, koken-ios-build), plus le
 * binaire ferme Median.
 *
 * Cette facade prefere `window.kokenBiometric` (pont maison, builds >= 63) et
 * retombe sur `window.median.auth` pour les builds anterieurs encore en
 * circulation. Les DEUX generations cohabitent pendant le deploiement ; le
 * repli Median disparaitra quand le parc aura migre.
 *
 * Ce que le pont maison corrige par construction, la ou Median nous a coute
 * cinq incidents en dix-huit jours : save = delete puis add EN NATIF (plus de
 * danse status/delete/save/retry ici), codes d'erreur sinceres, et secret
 * protege par .biometryCurrentSet (iOS l'invalide de lui-meme si la biometrie
 * de l'appareil change).
 *
 * Android : la coque maison n'a jamais expose de biometrie ; ce service reste
 * donc inerte sur Android tant que la parite n'est pas codee (KokenBiometric.kt).
 */

import { isMedianNative, getMedianPlatform } from '@/utils/medianNativeBridge';
import { bridgeErrorHandler } from './bridgeErrorHandler';
import { biometricIdentity } from './biometricIdentity';

// ============== Types ==============

export interface BiometricStatus {
  hasTouchId: boolean;
  hasSecret: boolean;
}

export interface BiometricSecretResult {
  success: boolean;
  secret?: string;
  error?: string;
}

// ============== Pont maison (KokenBiometricBridge.js) ==============

interface KokenBiometricBridge {
  isNative: () => boolean;
  status: () => Promise<{ hasTouchId: boolean; hasSecret: boolean; biometryType: 'faceId' | 'touchId' | 'none' }>;
  save: (options: { secret: string }) => Promise<{ success: true }>;
  get: (options: { prompt?: string }) => Promise<{ success: true; secret: string }>;
  delete: () => Promise<{ success: true }>;
}

declare global {
  interface Window {
    kokenBiometric?: KokenBiometricBridge;
  }
}

/** Le pont maison, s'il est joignable sur ce build. */
const kokenBridge = (): KokenBiometricBridge | null =>
  window.kokenBiometric && window.kokenBiometric.isNative() ? window.kokenBiometric : null;

// ============== Constants ==============

const BIOMETRIC_KEY = 'gam_pass_biometric_enabled';
const LAST_USER_EMAIL_KEY = 'gam_pass_last_user_email';

// ============== TEMPORARY FEATURE FLAG ==============
// Set to true to disable Median Biometric Auth until fully tested
const BIOMETRIC_TEMPORARILY_DISABLED = false;

// ============== Biometric Service ==============

export const medianBiometricAuth = {
  /**
   * Check if biometric authentication is available on this device
   */
  isAvailable: (): boolean => {
    if (BIOMETRIC_TEMPORARILY_DISABLED) return false;
    // Pont maison d'abord ; median.auth en repli pour les builds anterieurs.
    return !!kokenBridge() || (isMedianNative() && !!window.median?.auth);
  },

  /**
   * Get biometric status (has TouchID/FaceID and has stored secret)
   */
  getStatus: async (): Promise<BiometricStatus | null> => {
    if (!medianBiometricAuth.isAvailable()) {
      return null;
    }

    try {
      const bridge = kokenBridge();
      const status = bridge
        ? await bridge.status()
        : await window.median!.auth!.status();
      console.log('[BiometricAuth] Status:', status);
      return status;
    } catch (error) {
      console.warn('[BiometricAuth] Status check error:', error);
      return null;
    }
  },

  /**
   * Save refresh token securely with biometric protection
   */
  saveSecret: async (refreshToken: string, userEmail?: string): Promise<boolean> => {
    if (!medianBiometricAuth.isAvailable()) {
      console.warn('[BiometricAuth] Not available, cannot save secret');
      return false;
    }

    try {
      // Chemin maison : le natif fait delete puis add, l'ecriture ne peut pas
      // echouer pour cause de doublon et les codes d'erreur sont sinceres.
      // TOUTE la danse ci-dessous (status/delete/save/retry) n'existe que pour
      // contourner le plugin Median ; elle ne s'applique donc qu'au repli.
      const bridge = kokenBridge();
      if (bridge) {
        await bridge.save({ secret: refreshToken });
        localStorage.setItem(BIOMETRIC_KEY, 'true');
        if (userEmail) {
          localStorage.setItem(LAST_USER_EMAIL_KEY, userEmail);
        }
        console.log('[BiometricAuth] Secret saved (pont maison)');
        return true;
      }

      const platform = getMedianPlatform();
      const saveOptions: { secret: string; minimumAndroidBiometric?: 'strong' | 'weak' } = { secret: refreshToken };
      if (platform === 'android') {
        saveOptions.minimumAndroidBiometric = 'strong';
      }

      // ⚠️ Pas de delete() aveugle : un kill de l'app entre delete et save
      // laissait le Keychain vide → enrôlement Face ID perdu en silence
      // (« la carte a disparu »). On ne delete que si un secret EXISTE
      // (status().hasSecret), car le plugin iOS fait un SecItemAdd sec :
      // il échoue sur item existant SANS libellé fiable (revue 03/08 :
      // errSecDuplicateItem est mappé sur « itemNotFound », jamais
      // « duplicate » — conditionner le rattrapage à ce mot cassait la
      // rotation du secret à chaque TOKEN_REFRESHED). Filet : sur échec
      // du save direct, delete + re-save quel que soit le libellé.
      let hadSecret = false;
      try {
        const status = await window.median!.auth!.status();
        hadSecret = status?.hasSecret === true;
      } catch { /* status indisponible : on tente le save direct */ }

      if (hadSecret) {
        try { await window.median!.auth!.delete(); } catch { /* ignore */ }
      }
      let result = await window.median!.auth!.save(saveOptions);
      if (!result.success) {
        try { await window.median!.auth!.delete(); } catch { /* ignore */ }
        result = await window.median!.auth!.save(saveOptions);
      }

      if (result.success) {
        // Mark biometric as enabled for this user
        localStorage.setItem(BIOMETRIC_KEY, 'true');
        if (userEmail) {
          localStorage.setItem(LAST_USER_EMAIL_KEY, userEmail);
        }
        console.log('[BiometricAuth] Secret saved successfully');
      }
      
      return result.success;
    } catch (error) {
      console.warn('[BiometricAuth] Save secret error:', error);
      const errorType = bridgeErrorHandler.parseNativeError(error);
      bridgeErrorHandler.showError(errorType);
      return false;
    }
  },

  /**
   * Prompt biometric authentication and retrieve stored secret
   */
  getSecret: async (options?: { silent?: boolean; prompt?: string }): Promise<BiometricSecretResult> => {
    if (!medianBiometricAuth.isAvailable()) {
      if (!options?.silent) {
        bridgeErrorHandler.showError('BIOMETRIC_NOT_AVAILABLE');
      }
      return { success: false, error: 'Biometric not available' };
    }

    try {
      const bridge = kokenBridge();
      if (bridge) {
        try {
          const res = await bridge.get({ prompt: options?.prompt || 'Authentifiez-vous pour continuer' });
          return { success: true, secret: res.secret };
        } catch (err) {
          // Codes documentes du pont maison : user_canceled, auth_failed,
          // item_not_found (aucun secret, OU secret invalide par iOS apres un
          // changement de biometrie), interaction_not_allowed.
          const code = err instanceof Error ? err.message : String(err);
          if (!options?.silent && code !== 'user_canceled') {
            bridgeErrorHandler.showError(bridgeErrorHandler.parseNativeError(code));
          }
          return { success: false, error: code };
        }
      }

      const platform = getMedianPlatform();
      const getOptions: { prompt: string; callbackOnCancel: number; minimumAndroidBiometric?: 'strong' | 'weak' } = {
        prompt: options?.prompt || 'Authentifiez-vous pour continuer',
        callbackOnCancel: 1
      };
      if (platform === 'android') {
        getOptions.minimumAndroidBiometric = 'strong';
      }
      const result = await window.median!.auth!.get(getOptions);
      console.log('[BiometricAuth] Get secret result:', result.success ? 'OK' : result.error);
      
      if (!result.success && result.error) {
        const errorType = bridgeErrorHandler.parseNativeError(result.error);
        if (!options?.silent) {
          bridgeErrorHandler.showError(errorType);
        }
      }
      
      return result;
    } catch (error) {
      console.warn('[BiometricAuth] Get secret error:', error);
      const errorType = bridgeErrorHandler.parseNativeError(error);
      if (!options?.silent) {
        bridgeErrorHandler.showError(errorType);
      }
      return { success: false, error: String(error) };
    }
  },

  /**
   * Delete stored secret and disable biometric login
   */
  deleteSecret: async (): Promise<boolean> => {
    if (!medianBiometricAuth.isAvailable()) {
      // Just clear local flags
      localStorage.removeItem(BIOMETRIC_KEY);
      localStorage.removeItem(LAST_USER_EMAIL_KEY);
      return true;
    }

    try {
      const bridge = kokenBridge();
      const result = bridge
        ? await bridge.delete()
        : await window.median!.auth!.delete();
      localStorage.removeItem(BIOMETRIC_KEY);
      localStorage.removeItem(LAST_USER_EMAIL_KEY);
      console.log('[BiometricAuth] Secret deleted:', result.success);
      return result.success;
    } catch (error) {
      console.warn('[BiometricAuth] Delete secret error:', error);
      localStorage.removeItem(BIOMETRIC_KEY);
      localStorage.removeItem(LAST_USER_EMAIL_KEY);
      return false;
    }
  },

  /**
   * Check if biometric login was enabled by user
   */
  isEnabled: (): boolean => {
    return localStorage.getItem(BIOMETRIC_KEY) === 'true';
  },

  /**
   * Get last logged-in user email (for display purposes)
   */
  getLastUserEmail: (): string | null => {
    return localStorage.getItem(LAST_USER_EMAIL_KEY);
  },

  /**
   * Get friendly biometric type name.
   * ⚠️ Délègue à `biometricIdentity` (SSOT). Ne pas dupliquer la logique
   * de détection ici — modifier `src/services/biometricIdentity.ts`.
   */
  getBiometricTypeName: (): string => {
    return biometricIdentity.resolve().label;
  },

  /**
   * Check if device supports biometric and has it configured
   */
  canUseBiometric: async (): Promise<boolean> => {
    if (!medianBiometricAuth.isAvailable()) {
      return false;
    }

    const status = await medianBiometricAuth.getStatus();
    return status?.hasTouchId === true;
  },

  /**
   * Check if user has an active biometric session ready to use
   */
  hasActiveSession: async (): Promise<boolean> => {
    if (!medianBiometricAuth.isEnabled()) {
      return false;
    }

    const status = await medianBiometricAuth.getStatus();
    return status?.hasSecret === true;
  },
};

export default medianBiometricAuth;
